import { ExIcon } from '@boomi/exosphere';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import HelpText from '../common/HelpText';
import { translations } from '../translations/translations';
import type { Language } from '../types/translations';
import type { Option as OptionType, OptionValueType, Output } from '../types/types';
import { getValidationMessage } from '../utils/utils';
import triggerOptionFieldRegistry from './optionFields/triggerOptionFieldRegistry';

interface Props {
    option: OptionType;
    value: OptionValueType;
    onChange: (id: string, value: OptionValueType) => void;
    outputs?: Output[];
    showValidation: boolean;
    language?: Language;
}

const Option = ({ option, value, onChange, outputs, showValidation, language = 'en' }: Props) => {
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    useEffect(() => {
        setValidationMessage(getValidationMessage(option, value, language));
    }, [value, language, option]);

    const Input = triggerOptionFieldRegistry[option.type].component;

    // Figure out if we need to use a custom input and thus our own messages.
    const optionRequiresValidation = (option: OptionType) => {
        // These option types need a label as either:
        // - they will always use a custom input type
        // - the exo component doesn't include a label
        const componentOptionsRequiringLabel = [
            'SingleSelect', // Custom component options
            'MultiSelect',
            'SingleSelectButtons',
            'MultiSelectButtons',
            'Boolean', // Exo boolean doesn't have a label
        ];
        const stringOptions = ['String', 'StringMultiline'];
        const usesInputWithTokens = option.allowOutputs && stringOptions.includes(option.type);
        const usesTimeOffset = option.id === 'timeoffset';
        const usesComponentRequiringLabel = componentOptionsRequiringLabel.includes(option.type);

        return usesInputWithTokens || usesTimeOffset || usesComponentRequiringLabel;
    };

    const formFieldClasses = classNames('form-field', {
        'is-not-valid': showValidation && validationMessage && optionRequiresValidation(option),
    });

    return (
        <div className={formFieldClasses}>
            <label className="label xs" htmlFor={option.id}>
                {option.label}
                <span className={option.isRequired ? 'label-required' : 'label-optional'}>
                    {option.isRequired
                        ? translations[language].COMMON_required_label
                        : translations[language].COMMON_optional_label}
                </span>
            </label>
            <Input
                option={option}
                outputs={outputs}
                value={value}
                onChange={onChange}
                type={triggerOptionFieldRegistry[option.type].type}
                showValidation={showValidation}
                validationMessage={validationMessage}
            />
            {optionRequiresValidation(option) && (
                <section className="messages">
                    {showValidation && validationMessage && (
                        <p className="validation-message text xxs">
                            <ExIcon
                                className="validation-message-icon"
                                icon="Error"
                                hide-browser-tooltip
                            />
                            {validationMessage}
                        </p>
                    )}
                    {/* Exosphere hides help text if there is a validation error, so we'll match this behavior. */}
                    {!(showValidation && validationMessage) && option.help && (
                        <HelpText content={option.help} />
                    )}
                </section>
            )}
        </div>
    );
};

export default Option;
